import React, { useState } from 'react';
import '../utils/avatar-pricing-slider.css';
import '../utils/step-fade.css';

// ===== PRICING VARIABLES (Edit here) =====
const itoraPricing = {
  flatPerModelFee: 100,
  perProductFee: 10,
  perImage: 10,
};

const agencyPricing = {
  hourlyRate: 200,
  minHours: 4,
  shotsPerHour: 20,
};
// ========================================

// Slider min/max values
const PRODUCT_MIN = 1;
const PRODUCT_MAX = 20;
const POSE_MIN = 1;
const POSE_MAX = 5;
const AVATAR_MIN = 1;
const AVATAR_MAX = 20;

const StepPricingTool = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    numProducts: 1,
    imagesPerProduct: 1,
    numAvatars: 1,
    referencePhoto: null,
    name: '',
    email: '',
    company: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isAnimatingBack, setIsAnimatingBack] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Step order: Products, Poses, Avatars, Upload, Contact
  const steps = [
    { id: 'intro', label: 'Intro', required: false },
    { id: 'numProducts', label: 'Number of', sublabel: 'Products', required: true },
    { id: 'imagesPerProduct', label: 'Number of', sublabel: 'Poses', required: true },
    { id: 'numAvatars', label: 'Number of', sublabel: 'Avatars', required: true },
    { id: 'referencePhoto', label: 'Upload your', sublabel: 'Reference Photo', required: false },
    { id: 'email', label: 'Primary Contact', sublabel: 'How can we reach you?', required: true },
  ];

  const calculateCosts = () => {
    const itoraModelCost = formData.numAvatars * itoraPricing.flatPerModelFee;
    const itoraProductCost = formData.numProducts * itoraPricing.perProductFee;
    const itoraImageCost = formData.numProducts * formData.imagesPerProduct * itoraPricing.perImage;
    const itoraTotalCost = itoraModelCost + itoraProductCost + itoraImageCost;
    const totalShots = formData.numProducts * formData.imagesPerProduct;
    const agencyHours = Math.max(agencyPricing.minHours, totalShots / agencyPricing.shotsPerHour);
    const agencyTotalCost = agencyHours * agencyPricing.hourlyRate * formData.numAvatars;
    return {
      itora: itoraTotalCost,
      agency: agencyTotalCost,
      savings: agencyTotalCost - itoraTotalCost,
      savingsPercent: ((agencyTotalCost - itoraTotalCost) / agencyTotalCost) * 100,
    };
  };

  const handleInputChange = (field, value) => {
    if (field === 'referencePhoto') {
      if (value && value.size) {
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (value.size > maxSize) {
          alert('File size must be less than 5MB');
          return;
        }
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(value.type)) {
          alert('Please select an image file (JPEG, PNG, GIF, or WebP)');
          return;
        }
        const reader = new FileReader();
        reader.onload = (e) => {
          const base64Data = e.target.result.split(',')[1];
          setFormData(prev => ({
            ...prev,
            [field]: {
              file: value,
              data: base64Data,
              name: value.name,
              type: value.type
            }
          }));
        };
        reader.readAsDataURL(value);
      } else {
        setFormData(prev => ({ ...prev, [field]: value }));
      }
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(currentStep + 1);
        setIsAnimating(false);
      }, 300);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setIsAnimatingBack(true);
      setTimeout(() => {
        setCurrentStep(currentStep - 1);
        setIsAnimatingBack(false);
      }, 300);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const functionUrl = import.meta.env.VITE_FIREBASE_PRICING_FUNCTION_URL ||
        'https://us-central1-itora-da9d8.cloudfunctions.net/sendPricingRequest';
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          numAvatars: formData.numAvatars,
          numProducts: formData.numProducts,
          imagesPerProduct: formData.imagesPerProduct,
          name: formData.name,
          email: formData.email,
          company: formData.company,
          referencePhoto: formData.referencePhoto && formData.referencePhoto.data ? {
            data: formData.referencePhoto.data,
            name: formData.referencePhoto.name,
            type: formData.referencePhoto.type
          } : null
        }),
      });
      const result = await response.json();
      if (result.success) {
        setIsSubmitted(true);
        console.log('✅ Pricing request submitted successfully!', result);
      } else {
        setSubmitError(result.error || 'Failed to submit pricing request');
        console.error('Pricing request error:', result);
      }
    } catch (error) {
      console.error('Network error:', error);
      setSubmitError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper to extract main content, buttons, and progress from each step
  const getStepParts = (stepId) => {
    switch (stepId) {
      case 'intro':
        return {
          content: (
            <div style={{ maxWidth: 600, textAlign: 'center', color: 'var(--c0, #fff)', padding: '2rem', width: '100%' }}>
              <h1 style={{ fontSize: '2.8rem', fontWeight: 800, marginBottom: '1.5rem', letterSpacing: '-0.01em', textTransform: 'uppercase' }}>JOIN BETA</h1>
              <p style={{ fontSize: 'inherit', marginBottom: '2.5rem', color: 'var(--c0, #fff)' }}>
                Our pricing is currently project based, while we develop an API based pricing system. Provide details for your project, we will provide a free demo and project quote.
              </p>
              <button type="button" className="btn nav-cta" style={{ fontSize: '2rem', padding: '1rem 3rem', fontWeight: 700, background: 'var(--c4, #7360ff)', color: 'var(--c0, #fff)', border: 'none', borderRadius: 8, textDecoration: 'none', boxShadow: '0 2px 18px rgba(0,0,0,0.10)' }} onClick={nextStep}>
                Get Started
              </button>
            </div>
          ),
          buttons: null,
          progress: null
        };
      case 'numProducts': {
        const value = Number(formData.numProducts);
        return {
          content: (
            <>
              <div className="my-4">
                <div className="fs-6 c-c0 mb-1">Number of</div>
                <h3 className="c-c0 mb-0 f-uc">Products</h3>
              </div>
              <p className="c-c1 mb-4">How many different products are you considering?</p>
              <div className="mb-2">
                <span className="fs-2 f-b" style={{ color: 'var(--c0)' }}>{value === PRODUCT_MAX ? '20+' : value}</span>
              </div>
              <input
                type="range"
                min={PRODUCT_MIN}
                max={PRODUCT_MAX}
                step={1}
                value={value}
                onChange={e => handleInputChange('numProducts', Number(e.target.value))}
                className="pricing-slider w-100 my-3"
                style={{ maxWidth: 400, '--percent': `${((value - PRODUCT_MIN) / (PRODUCT_MAX - PRODUCT_MIN)) * 100}` }}
              />
              <div className="d-flex flex-row justify-content-between w-100" style={{ maxWidth: 400, margin: '0 auto', fontSize: 14, color: 'var(--c1)' }}>
                <span>1</span>
                <span>5</span>
                <span>10</span>
                <span>15</span>
                <span>20+</span>
              </div>
            </>
          ),
          buttons: (
            <div className="d-flex justify-content-center gap-3 mt-4">
              <button type="button" className="btn btn-outline-light btn-lg" onClick={prevStep} disabled={currentStep === 0}>Back</button>
              <button type="button" className="btn btn-light text-dark btn-lg" onClick={nextStep} disabled={!formData.numProducts}>Next</button>
            </div>
          ),
          progress: <div className="mt-2">{renderProgressIndicator()}</div>
        };
      }
      case 'imagesPerProduct': {
        const value = Number(formData.imagesPerProduct);
        return {
          content: (
            <>
              <div className="my-4">
                <div className="fs-6 c-c0 mb-1">Number of</div>
                <h3 className="c-c0 mb-0 f-uc">Poses</h3>
              </div>
              <p className="c-c1 mb-4">How many different poses for each product?</p>
              <div className="mb-2">
                <span className="fs-2 f-b" style={{ color: 'var(--c0)' }}>{value === POSE_MAX ? '5+' : value}</span>
              </div>
              <input
                type="range"
                min={POSE_MIN}
                max={POSE_MAX}
                step={1}
                value={value}
                onChange={e => handleInputChange('imagesPerProduct', Number(e.target.value))}
                className="pricing-slider w-100 my-3"
                style={{ maxWidth: 400, '--percent': `${((value - POSE_MIN) / (POSE_MAX - POSE_MIN)) * 100}` }}
              />
              <div className="d-flex flex-row justify-content-between w-100" style={{ maxWidth: 400, margin: '0 auto', fontSize: 14, color: 'var(--c1)' }}>
                <span>1</span>
                <span>2</span>
                <span>3</span>
                <span>4</span>
                <span>5+</span>
              </div>
            </>
          ),
          buttons: (
            <div className="d-flex justify-content-center gap-3 mt-4">
              <button type="button" className="btn btn-outline-light btn-lg" onClick={prevStep} disabled={currentStep === 0}>Back</button>
              <button type="button" className="btn btn-light text-dark btn-lg" onClick={nextStep} disabled={!formData.imagesPerProduct}>Next</button>
            </div>
          ),
          progress: <div className="mt-2">{renderProgressIndicator()}</div>
        };
      }
      case 'numAvatars': {
        const value = Number(formData.numAvatars);
        return {
          content: (
            <>
              <div className="my-4">
                <div className="fs-6 c-c0 mb-1">Number of</div>
                <h3 className="c-c0 mb-0 f-uc">Avatars</h3>
              </div>
              <p className="c-c1 mb-4">How many different avatars would you like?</p>
              <div className="mb-2">
                <span className="fs-2 f-b" style={{ color: 'var(--c0)' }}>{value === AVATAR_MAX ? '20+' : value}</span>
              </div>
              <input
                type="range"
                min={AVATAR_MIN}
                max={AVATAR_MAX}
                step={1}
                value={value}
                onChange={e => handleInputChange('numAvatars', Number(e.target.value))}
                className="pricing-slider w-100 my-3"
                style={{ maxWidth: 400, '--percent': `${((value - AVATAR_MIN) / (AVATAR_MAX - AVATAR_MIN)) * 100}` }}
              />
              <div className="d-flex flex-row justify-content-between w-100" style={{ maxWidth: 400, margin: '0 auto', fontSize: 14, color: 'var(--c1)' }}>
                <span>1</span>
                <span>5</span>
                <span>10</span>
                <span>15</span>
                <span>20+</span>
              </div>
            </>
          ),
          buttons: (
            <div className="d-flex justify-content-center gap-3 mt-4">
              <button type="button" className="btn btn-outline-light btn-lg" onClick={prevStep} disabled={currentStep === 0}>Back</button>
              <button type="button" className="btn btn-light text-dark btn-lg" onClick={nextStep} disabled={!formData.numAvatars}>Next</button>
            </div>
          ),
          progress: <div className="mt-2">{renderProgressIndicator()}</div>
        };
      }
      case 'referencePhoto': {
        return {
          content: (
            <>
              <div className="my-4">
                <div className="fs-6 c-c0 mb-1">Upload your</div>
                <h3 className="c-c0 mb-0 f-uc">Reference Photo</h3>
              </div>
              <p className="c-c1 mb-4">Share a product photo or reference image (optional, 5MB max).</p>
              <div className="d-flex flex-column align-items-center mb-4" style={{ width: '100%' }}>
                <input
                  type="file"
                  className="form-control form-control-lg"
                  style={{ width: '100%', maxWidth: '340px' }}
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={(e) => handleInputChange('referencePhoto', e.target.files[0])}
                  disabled={isSubmitting}
                />
                {formData.referencePhoto && formData.referencePhoto.name && (
                  <div className="mt-2 small c-c0">Selected: {formData.referencePhoto.name}</div>
                )}
              </div>
            </>
          ),
          buttons: (
            <div className="d-flex justify-content-center gap-3 mt-4">
              <button type="button" className="btn btn-outline-light btn-lg" onClick={prevStep} disabled={currentStep === 0}>Back</button>
              <button type="button" className="btn btn-light text-dark btn-lg" onClick={nextStep}>Next</button>
            </div>
          ),
          progress: <div className="mt-2">{renderProgressIndicator()}</div>
        };
      }
      case 'email':
        return {
          content: (
            <>
              <div className="my-4">
                <div className="fs-6 c-c0 mb-1">How can we reach you?</div>
                <h3 className="c-c0 mb-0 f-uc">Contact</h3>
              </div>
              <p className="c-c1 mb-4">We'll send your detailed estimate and demo to this email address.</p>
              <div className="row justify-content-center align-items-start w-100">
                {/* Contact Form (Left) */}
                <div className="col-12 col-md-6 mb-4 mb-md-0">
                  <div className="d-flex flex-column align-items-center gap-3 mb-4" style={{ maxWidth: 400, margin: '0 auto' }}>
                    <input
                      type="text"
                      className="form-control form-control-sm f-b"
                      style={{ fontSize: 15 }}
                      placeholder="Name"
                      value={formData.name}
                      onChange={e => handleInputChange('name', e.target.value)}
                      required
                    />
                    <input
                      type="text"
                      className="form-control form-control-sm f-b"
                      style={{ fontSize: 15 }}
                      placeholder="Company (optional)"
                      value={formData.company}
                      onChange={e => handleInputChange('company', e.target.value)}
                    />
                    <input
                      type="email"
                      className="form-control form-control-sm f-b"
                      style={{ fontSize: 15 }}
                      placeholder="Email"
                      value={formData.email}
                      onChange={e => handleInputChange('email', e.target.value)}
                      required
                    />
                  </div>
                </div>
                {/* Summary (Right) */}
                <div className="col-12 col-md-6">
                  <div style={{ maxWidth: 400, margin: '0 auto', background: 'none', padding: 0 }}>
                    <ul className="list-unstyled mb-0 c-c0" style={{ marginTop: 0, paddingTop: 0 }}>
                      <li className="mb-2"><span className="f-b f-uc">PRODUCTS:</span> {formData.numProducts === 20 ? '20+' : formData.numProducts}</li>
                      <li className="mb-2"><span className="f-b f-uc">POSES:</span> {formData.imagesPerProduct === 5 ? '5+' : formData.imagesPerProduct}</li>
                      <li className="mb-2"><span className="f-b f-uc">AVATARS:</span> {formData.numAvatars === 20 ? '20+' : formData.numAvatars}</li>
                      <li className="mb-2"><span className="f-b f-uc">IMAGE:</span> {formData.referencePhoto && formData.referencePhoto.name ? formData.referencePhoto.name : 'None'}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </>
          ),
          buttons: (
            <div className="d-flex justify-content-center gap-3">
              <button type="button" className="btn btn-outline-light btn-lg" onClick={prevStep} disabled={currentStep === 0}>Back</button>
              <button type="submit" className="btn btn-light text-dark btn-lg" disabled={!formData.name || !formData.email || isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          ),
          progress: <div className="d-flex justify-content-center">{renderProgressIndicator()}</div>
        };
      default:
        return { content: null, buttons: null, progress: null };
    }
  };

  // Progress indicator (unchanged, but centered for new layout)
  const renderProgressIndicator = () => {
    // Only show progress for steps after the intro
    const progressSteps = steps.filter(step => step.id !== 'intro');
    const progressIndex = Math.max(0, currentStep - 1); // -1 because intro is not counted
    return (
      <div className="d-flex justify-content-center align-items-center pb-4 my-4">
        {progressSteps.map((step, index) => (
          <div key={index} className="d-flex align-items-center">
            <div
              className={`rounded-circle d-flex align-items-center justify-content-center f-b ${
                index < progressIndex
                  ? 'c-c0'
                  : index === progressIndex
                  ? 'c-c0'
                  : 'c-c0'
              }`}
              style={{
                lineHeight: '1',
                width: '18px',
                height: '18px',
                fontSize: '10px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                backgroundColor: 'transparent',
                border: '1px solid var(--c0)',
                color: 'var(--c0)',
                opacity: index < progressIndex ? 0.6 : index === progressIndex ? 1 : 0.6
              }}
              onClick={() => setCurrentStep(index + 1)} // +1 to skip intro
              onMouseEnter={(e) => {
                if (index !== progressIndex) {
                  e.target.style.transform = 'scale(1.1)';
                  e.target.style.opacity = '1';
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
                e.target.style.opacity = index < progressIndex ? '0.6' : index === progressIndex ? '1' : '0.6';
              }}
            >
              {index < progressIndex ? '✓' : index + 1}
            </div>
            {index < progressSteps.length - 1 && (
              <div
                className="c-c0 mx-2"
                style={{
                  fontSize: '16px',
                  opacity: 0.6
                }}
              >
                —
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  if (isSubmitted) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center w-100" style={{ minHeight: '100vh' }}>
        <div className="card b-c5 c-c0" style={{ maxWidth: 540, width: '100%' }}>
          <div className="card-body text-center">
            <h2 className="c-c0 mb-3">Request Submitted!</h2>
            <p className="px-5 fs-7 c-c0 fs-5 text-center">
              Hang tight while we estimate your project cost and prepare a product demo.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const { content, buttons, progress } = getStepParts(steps[currentStep].id);
  return (
    <div className="d-flex flex-column justify-content-center align-items-center w-100" style={{ minHeight: '100vh' }}>
      <form onSubmit={handleSubmit} style={{ width: '100%', maxWidth: 540 }}>
        {submitError && (
          <div className="alert alert-danger mb-3">
            <strong>Error:</strong> {submitError}
          </div>
        )}
        <div className="d-flex flex-column align-items-center justify-content-between text-center step-pricing-tool-step-container" style={{ height: '75vh', width: '100%', maxHeight: '100vh', padding: 0 }}>
          <div className={`w-100 flex-grow-1 d-flex flex-column justify-content-center align-items-center step-fade${isAnimating ? ' fade-out' : ''}${isAnimatingBack ? ' fade-out' : ''}`}>{content}</div>
          <div style={{ width: '100%' }}>
            {buttons}
            {progress}
          </div>
        </div>
      </form>
    </div>
  );
};

export default StepPricingTool; 